import { CalmGuide } from '@/libs/api/create-calm-public-api/types';
import { MediaGuideMetadata } from '@/utils/media/types';
import isSSR from '@calm/web-common/dist/isSSR';
import { HOUR_S, MINUTE_MS, MINUTE_S } from '@calm/web-common/dist/time';
import { VideoMediaType } from '@rhg/components/media/types';

export const MEDIA_STALE_TIME = 15 * MINUTE_MS;

/** Default (normal) playback rate. */
export const NORMAL_PLAYBACK_RATE = 1;

/** Supported playback rates, in display order. Matches iOS and Android. */
export const PLAYBACK_RATES = [0.5, 0.8, 1, 1.2, 1.5, 2];

/**
 * Formats a playback rate to a one-decimal string (e.g. `1` -> `'1.0'`).
 * The trailing `x` is added by the i18n message, not here.
 */
export function formatPlaybackRate(rate: number): string {
    return rate.toFixed(1);
}

/** Deliberately terse/opaque key so the stored value's purpose isn't obvious. */
export const PLAYBACK_RATE_STORAGE_KEY = 'pbr';

/**
 * Reads the persisted playback rate from sessionStorage (session-scoped, no PHI).
 * Only values in the supported `PLAYBACK_RATES` set are honored; anything else
 * (corrupt, hand-edited, or from an older version) falls back to normal speed.
 */
export function readStoredPlaybackRate(): number {
    if (isSSR()) return NORMAL_PLAYBACK_RATE;
    const parsed = Number(sessionStorage.getItem(PLAYBACK_RATE_STORAGE_KEY));
    return PLAYBACK_RATES.includes(parsed) ? parsed : NORMAL_PLAYBACK_RATE;
}

/** Persists the playback rate to sessionStorage. Only supported rates are stored. */
export function writeStoredPlaybackRate(rate: number): void {
    if (isSSR() || !PLAYBACK_RATES.includes(rate)) return;
    sessionStorage.setItem(PLAYBACK_RATE_STORAGE_KEY, String(rate));
}

export enum AudioEvent {
    LoadedMetadata = 'loadedmetadata',
    Play = 'play',
    Pause = 'pause',
    Ended = 'ended',
    TimeUpdate = 'timeupdate',
    Progress = 'progress',
    Seeked = 'seeked',
}

export enum VideoEvent {
    LoadedMetadata = 'loadedmetadata',
    Play = 'play',
    Pause = 'pause',
    Ended = 'ended',
    TimeUpdate = 'timeupdate',
    Progress = 'progress',
    Seeked = 'seeked',
}

export enum MediaType {
    Audio = 'audio',
    Video = 'video',
    Unknown = 'unknown',
}

export const MediaEvent = { ...AudioEvent, ...VideoEvent };
export type MediaEvent = AudioEvent | VideoEvent;

const DEFAULT_PUBLIC_ASSETS_URLS = ['https://assets.calm.com'];

export const CALM_PUBLIC_ASSETS_URLS: string[] = (() => {
    const raw = process.env.NEXT_PUBLIC_CALM_PUBLIC_ASSETS_BASEURLS;
    if (!raw) {
        return DEFAULT_PUBLIC_ASSETS_URLS;
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
        ? parsed.filter((item): item is string => typeof item === 'string')
        : DEFAULT_PUBLIC_ASSETS_URLS;
})();

export function needsMediaCredentials(src: string) {
    return !CALM_PUBLIC_ASSETS_URLS.some((publicUrl) =>
        src.includes(publicUrl)
    );
}

/** Maps a video URL to its mime type, defaulting to mp4 when not HLS. */
export function getVideoMediaType(url: string): VideoMediaType {
    return url.endsWith('.m3u8') ? 'application/x-mpegURL' : 'video/mp4';
}

export function displayTime(s: number) {
    const hours = Math.floor(s / HOUR_S);
    const minutes = Math.floor((s % HOUR_S) / MINUTE_S);
    const seconds = Math.floor(s % MINUTE_S);
    return `${hours ? hours + ':' : ''}${
        hours && minutes < 10 ? '0' + minutes : minutes
    }:${seconds < 10 ? '0' + seconds : seconds}`;
}

export function getMediaType(guide: MediaGuideMetadata): MediaType {
    for (const { type } of guide.assets) {
        if (type === 'audio') {
            return MediaType.Audio;
        }

        if (type === 'video') {
            return MediaType.Video;
        }
    }

    return MediaType.Unknown;
}

// For some reason we cannot use the URL constructor in jest/node so we are implementing it manually
export function getM3U8ProxyUrl(url: string, credentials: string) {
    const urlParts = url.split('?');
    const pathname = urlParts[0].split('/').slice(3).join('/');
    return `${process.env.NEXT_PUBLIC_CALM_API_BASEURL}/content/core/m3u8/${pathname}?${credentials}`;
}

export function getGuideId(guide: MediaGuideMetadata | CalmGuide) {
    return 'id' in guide ? guide.id : guide.guideId;
}

export function getGuideDuration(
    guide: MediaGuideMetadata
): number | undefined {
    return 'duration' in guide
        ? guide.duration
        : (guide as { totalDuration: number }).totalDuration;
}

export function getGuideProgressDuration(
    guide: MediaGuideMetadata
): number | undefined {
    return 'progressDuration' in guide
        ? (guide as { progressDuration: number }).progressDuration
        : guide.progress;
}

/**
 * Fraction of the guide completed (0–1, rounded to 2 decimals) for the
 * `PercentCompleted` analytics property — parity with iOS
 * (`Float(observedTime) / guideLength` rounded to 2 digits) and Android.
 * Position and duration must share a unit (both ms or both seconds).
 * Returns 0 when the duration is unknown or zero.
 */
export function toPercentCompleted(
    position: number | undefined,
    duration: number | undefined
): number {
    if (!duration || duration <= 0) {
        return 0;
    }
    const fraction = (position ?? 0) / duration;
    return Math.round(Math.min(Math.max(fraction, 0), 1) * 100) / 100;
}
